<template>
  <!-- 碳交易所平台主应用 -->
  <div id="app" class="min-h-screen font-sans">
    <n-config-provider :theme-overrides="themeOverrides">

      <!-- 全局错误处理器 -->
      <GlobalErrorHandler />

      <!-- 离线状态检测器 -->
      <OfflineDetector />

      <!-- 预加载屏幕已移除 -->

      <!-- 认证初始化错误提示 -->
      <div v-if="showAuthError" class="fixed top-4 right-4 z-50">
        <n-alert type="warning" closable @close="dismissAuthError" class="max-w-sm">
          <template #header>认证初始化警告</template>
          <div>
            {{ authErrorMessage }}
            <div class="mt-2">
              <n-button size="small" @click="retryAuthInitialization">
                重试
              </n-button>
            </div>
          </div>
        </n-alert>
      </div>

      <!-- 主应用内容 - 始终显示 -->
      <div class="app-content">
        <!-- 全局消息提供者 -->
        <n-message-provider>
          <n-notification-provider>
            <n-dialog-provider>
              <!-- 认证状态指示器（开发模式） -->
              <div v-if="showAuthDebugInfo" class="fixed bottom-4 left-4 z-40">
                <n-card size="small" class="max-w-xs">
                  <template #header>认证状态</template>
                  <div class="text-xs space-y-1">
                    <div>状态: {{ authStatusText }}</div>
                    <div v-if="authStore.isInitializing">
                      <n-spin size="small" />
                      <span class="ml-2">初始化中...</span>
                    </div>
                    <div v-if="authStore.lastError" class="text-red-500">
                      错误: {{ authStore.lastError.message }}
                    </div>
                  </div>
                </n-card>
              </div>

              <!-- 主内容区域 -->
              <main id="main-content" tabindex="-1">
                <!-- 路由视图 -->

                <router-view />

              </main>

              <!-- 调试面板 (仅开发环境) -->
              <!-- <DebugPanel v-if="isDev" /> -->

            </n-dialog-provider>
          </n-notification-provider>
        </n-message-provider>
      </div>
    </n-config-provider>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  NMessageProvider,
  NNotificationProvider,
  NDialogProvider,
  NAlert,
  NButton,
  NCard,
  NSpin,
  NConfigProvider
} from 'naive-ui'
import { useAuthStore } from '@/stores/auth'
// import AccessibilityEnhancer from '@/components/common/AccessibilityEnhancer.vue'
import GlobalErrorHandler from '@/components/common/GlobalErrorHandler.vue'
import OfflineDetector from '@/components/common/OfflineDetector.vue'
import { Input } from 'postcss'
// import DebugPanel from '@/components/DebugPanel.vue'


const authStore = useAuthStore()
const router = useRouter()

// 预加载功能已移除

// 认证错误处理状态
const showAuthError = ref(false)
const authErrorMessage = ref('')
const authErrorDismissed = ref(false)

// 开发模式调试信息 - 默认关闭，需要手动开启
const showAuthDebugInfo = ref(false)

// 认证状态文本
const authStatusText = computed(() => {
  if (authStore.isInitializing) return '初始化中'
  if (authStore.isAuthenticated) return '已认证'
  return '未认证'
})

// 监听认证错误状态变化
watch(
  () => authStore.lastError,
  (newError) => {
    if (newError && !authErrorDismissed.value) {
      // 只显示关键错误，避免干扰用户体验
      if (newError.type === 'NETWORK_ERROR' || newError.type === 'TIMEOUT_ERROR') {
        authErrorMessage.value = newError.message
        showAuthError.value = true
      }
    } else if (!newError) {
      // 错误已清除
      showAuthError.value = false
      authErrorDismissed.value = false
    }
  },
  { immediate: true }
)

// 监听认证状态变化，提供用户反馈
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated, wasAuthenticated) => {
    // 只在状态真正改变时提供反馈
    if (wasAuthenticated !== undefined && wasAuthenticated !== isAuthenticated) {
      if (isAuthenticated) {
        console.log('[App] 用户已认证')
      } else {
        console.log('[App] 用户认证状态已清除')
      }
    }
  }
)

// 预加载处理函数已移除

// 启动认证初始化（非阻塞）
const startAuthInitialization = () => {
  // 检查是否有令牌需要验证
  if (authStore.token) {
    console.log('[App] 发现本地令牌，开始非阻塞认证初始化')

    // 非阻塞地初始化认证状态
    authStore.initAuth().catch(error => {
      console.warn('[App] 认证初始化失败，但应用继续运行:', error)

      // 不显示所有错误，只显示需要用户关注的错误
      if (error?.type === 'NETWORK_ERROR' && !authErrorDismissed.value) {
        authErrorMessage.value = '网络连接问题，某些功能可能受限'
        showAuthError.value = true
      }
    })
  } else {
    console.log('[App] 没有本地令牌，跳过认证初始化')
  }
}

// 重试认证初始化
const retryAuthInitialization = () => {
  console.log('[App] 用户请求重试认证初始化')
  dismissAuthError()
  startAuthInitialization()
}

// 关闭认证错误提示
const dismissAuthError = () => {
  showAuthError.value = false
  authErrorDismissed.value = true
}

// 键盘事件处理器
let keyboardHandler: ((e: KeyboardEvent) => void) | null = null

// 检查认证状态并决定初始导航
const checkInitialNavigation = () => {
  // 如果当前路径不是根路径，不进行自动导航
  if (router.currentRoute.value.path !== '/') {
    console.log('[App] 当前路径不是根路径，跳过自动导航:', router.currentRoute.value.path)
    return
  }

  // 等待认证状态初始化完成后再进行导航判断
  if (authStore.isInitializing) {
    console.log('[App] 认证正在初始化中，等待完成后再导航')
    return
  }

  if (authStore.isAuthenticated) {
    console.log('[App] 用户已认证，检查问卷状态')

    // 尝试修复数据不一致问题
    authStore.fixDataInconsistency()

    // 检查问卷完成状态
    authStore.checkQuestionnaireStatus().then(hasCompleted => {
      if (hasCompleted) {
        console.log('[App] 用户已完成问卷，导航到仪表板')
        router.replace('/dashboard')
      } else {
        console.log('[App] 用户需要完成问卷，导航到问卷页面')
        router.replace('/questionnaire')
      }
    }).catch(error => {
      console.error('[App] 检查问卷状态失败:', error)
      // 发生错误时，默认导航到仪表板
      router.replace('/dashboard')
    })
  } else {
    console.log('[App] 用户未认证，保持在首页')
  }
}

const themeOverrides = {
  common: {
    themePrimary: "#2f2c7f",
    primaryColor: "#2f2c7f",
    successColor: "#2f2c7f",
    nCaretColor: "#2f2c7f",
    nLoadingColor: "#2f2c7f",
    nBorderHover: "1px solid #2f2c7f",
    nBorderFocus: "1px solid #2f2c7f",
  },
  Tabs: {
    tabTextColorActiveBar: 'red',//方法二：单独设置
    barColor: 'yellow'
  }
}


// 应用初始化
onMounted(async () => {
  console.log('[App] 应用挂载完成，开始初始化')

  // 始终启动认证初始化（非阻塞）
  startAuthInitialization()

  // 启动智能路由预加载
  const { smartPreload } = await import('@/router/preloader')
  smartPreload()

  // 检查是否有初始导航目标（来自 InitialLoader）
  const initialTarget = sessionStorage.getItem('initial_navigation_target')
  if (initialTarget) {
    console.log('[App] 发现初始导航目标:', initialTarget)
    sessionStorage.removeItem('initial_navigation_target')

    // 立即导航，不等待认证初始化
    console.log('[App] 立即导航到目标路由:', initialTarget)
    router.replace(initialTarget)
    return
  }

  // 等待认证初始化完成后检查导航
  console.log('[App] 等待认证初始化完成')

  // 监听认证初始化完成
  const stopWatching = watch(
    () => authStore.isInitializing,
    (isInitializing) => {
      if (!isInitializing) {
        console.log('[App] 认证初始化完成，检查初始导航')
        setTimeout(() => {
          checkInitialNavigation()
          stopWatching() // 停止监听
        }, 100)
      }
    },
    { immediate: true }
  )

  // 开发环境下添加快捷键
  if (import.meta.env.DEV) {
    keyboardHandler = (e: KeyboardEvent) => {
      // 预加载重置快捷键已移除

      // Ctrl + Shift + D 切换调试信息显示
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault()
        showAuthDebugInfo.value = !showAuthDebugInfo.value
        console.log('[App] 认证调试信息显示:', showAuthDebugInfo.value)
      }

      // Ctrl + Shift + R 重试认证初始化
      if (e.ctrlKey && e.shiftKey && e.key === 'R') {
        e.preventDefault()
        retryAuthInitialization()
      }
    }

    window.addEventListener('keydown', keyboardHandler)
  }
})

// 清理资源
onUnmounted(() => {
  if (keyboardHandler) {
    window.removeEventListener('keydown', keyboardHandler)
  }
})
</script>
